import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import API from "../../services/api.js";
import LoadingPopup from "../misc/Loading.js";
import { createPortal } from "react-dom";

// Styled tooltip rendered into a portal so it escapes the table's overflow
// container (which otherwise clips an absolutely-positioned popover). Positioned
// with fixed coordinates from the trigger's bounding rect — above and centered.
const HoverTooltip = ({ text, children, className = "" }) => {
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);

  const show = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ top: rect.top, left: rect.left + rect.width / 2 });
  };
  const hide = () => setPos(null);

  return (
    <div
      ref={triggerRef}
      className={`flex-shrink-0 ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {children}
      {pos &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: pos.top - 8,
              left: pos.left,
              transform: "translate(-50%, -100%)",
            }}
            className="z-[9999] max-w-[280px] px-2.5 py-1.5 bg-[#1a1a1c] border border-gray-700 text-gray-200 text-xs rounded-lg shadow-xl pointer-events-none"
          >
            {text}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-700" />
          </div>,
          document.body
        )}
    </div>
  );
};

const Vulnerabilities = () => {
  const [data, setData] = useState([]);
  const [falsePositives, setFalsePositives] = useState([]);
  const [passCases, setPassCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [selectedFalsePositives, setSelectedFalsePositives] = useState([]);
  const [showFalsePositives, setShowFalsePositives] = useState(false);
  const [showPassCases, setShowPassCases] = useState(false);
  const [severityEdits, setSeverityEdits] = useState({});
  const [initialSeverityMap, setInitialSeverityMap] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    prompt: "",
    response: "",
    evaluation: "",
    severity: "medium"
  });
  const [addError, setAddError] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [showFpFeedback, setShowFpFeedback] = useState(false);
  const [fpFeedback, setFpFeedback] = useState("");
  const [fpSubmitting, setFpSubmitting] = useState(false);
  const [fpFeedbackError, setFpFeedbackError] = useState("");
  // Right-side conversation drawer for multi-turn findings.
  // null = closed; otherwise { messages: [...], title: "..." }.
  const [convo, setConvo] = useState(null);
  const drawerRef = useRef(null);
  const convoTriggerRef = useRef(null);
  const navigate = useNavigate();

  // Conversation drawer: ESC to close, move focus into the panel + trap Tab while
  // open, lock background scroll, and restore focus to the trigger on close.
  useEffect(() => {
    if (!convo) return;
    const previouslyFocused = convoTriggerRef.current || document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusablesIn = (el) =>
      el
        ? el.querySelectorAll(
            'button, [href], input, textarea, [tabindex]:not([tabindex="-1"])'
          )
        : [];
    (focusablesIn(drawerRef.current)[0] || drawerRef.current)?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") {
        setConvo(null);
        return;
      }
      if (e.key !== "Tab" || !drawerRef.current) return;
      const f = focusablesIn(drawerRef.current);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }, [convo]);

  const { filename } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const type_filter = decodeURIComponent(queryParams.get("type_filter"));
  console.log("Type Filter:", type_filter);
  const selected_app = sessionStorage.getItem("selectedApp");

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    setError("");
    setSaveMessage("");
    try {
      const url = `${process.env.REACT_APP_DJANGO_API_URL}/vulnerabilities/${filename}.json/?type_filter=${encodeURIComponent(type_filter)}&selected_app=${selected_app}`;
      const response = await API.get(url);
      const jsonData = response.data;
      if (Array.isArray(jsonData.results)) {
        const vulnerabilities = jsonData.results.filter(
          (item) => item.status === "Fail" && !item.isFalsePositive
        );
        const falsePositiveData = jsonData.results.filter(
          (item) => item.isFalsePositive
        );
        const passData = jsonData.results.filter(
          (item) => item.status === "Pass" && !item.isFalsePositive
        );
        setData(vulnerabilities);
        setFalsePositives(falsePositiveData);
        setPassCases(passData);

        const nextSeverityMap = {};
        jsonData.results.forEach((row) => {
          nextSeverityMap[row.testIdx] = normalizeSeverityValue(
            row.score || row.severity || "info"
          );
        });
        setInitialSeverityMap(nextSeverityMap);
        setSeverityEdits({});
      } else {
        throw new Error("Invalid data format");
      }
    } catch (err) {
      setError("Failed to fetch vulnerabilities.");
    } finally {
      setLoading(false);
    }
  }, [filename, type_filter, selected_app]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleCheckboxChange = (testIdx, isChecked) => {
    if (isChecked) {
      setSelectedPrompts((prev) => [...prev, testIdx]);
    } else {
      setSelectedPrompts((prev) => prev.filter((id) => id !== testIdx));
    }
  };

  const handleFalsePositiveCheckboxChange = (testIdx, isChecked) => {
    if (isChecked) {
      setSelectedFalsePositives((prev) => [...prev, testIdx]);
    } else {
      setSelectedFalsePositives((prev) => prev.filter((id) => id !== testIdx));
    }
  };

  const handleSelectAllChange = (isChecked) => {
    if (isChecked) {
      setSelectedPrompts(data.map((row) => row.testIdx));
    } else {
      setSelectedPrompts([]);
    }
  };

  const handleSubmit = async () => {
    if (selectedPrompts.length === 0) return;
    setFpFeedbackError("");
    setFpFeedback("");
    setShowFpFeedback(true);
  };

  const handleFpFeedbackSubmit = async () => {
    if (!fpFeedback.trim()) {
      setFpFeedbackError("Please add a short reason.");
      return;
    }
    setFpSubmitting(true);
    setFpFeedbackError("");
    try {
      await API.post("/mark-false-positive/", {
        filename: `${filename}.json`,
        selected_app,
        test_indices: selectedPrompts,
        is_false_positive: true,
        fp_feedback: fpFeedback.trim()
      });
      await fetchDetails();
      setSelectedPrompts([]);
      setShowFpFeedback(false);
      setFpFeedback("");
    } catch (err) {
      setFpFeedbackError("Failed to mark as false positive.");
    } finally {
      setFpSubmitting(false);
    }
  };

  const handleFpFeedbackClose = () => {
    setShowFpFeedback(false);
    setFpFeedback("");
    setFpFeedbackError("");
  };

  const handleUnmarkFalsePositive = async () => {
    setLoading(true);
    try {
      await API.post("/mark-false-positive/", {
        filename: `${filename}.json`,
        selected_app,
        test_indices: selectedFalsePositives,
        is_false_positive: false
      });
      await fetchDetails();
      setSelectedFalsePositives([]);
    } catch (err) {
      setError("Failed to unmark false positive.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSeverity = async () => {
    const updates = Object.entries(severityEdits).map(([testIdx, severity]) => ({
      test_idx: Number.isNaN(Number(testIdx)) ? testIdx : Number(testIdx),
      severity
    }));
    if (updates.length === 0) return;
    setIsSaving(true);
    setSaveMessage("");
    try {
      await API.post("/update-severity/", {
        filename: `${filename}.json`,
        selected_app,
        updates
      });
      setSaveMessage("Severity updates saved.");
      await fetchDetails();
    } catch (err) {
      setSaveMessage("Failed to save severity updates.");
    } finally {
      setIsSaving(false);
    }
  };

  const openAddModal = () => {
    setAddError("");
    setAddForm({
      prompt: "",
      response: "",
      evaluation: "",
      severity: "medium"
    });
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddError("");
  };

  const handleAddVulnerability = async () => {
    const presetType = resolvePresetType();
    if (!presetType) {
      setAddError("Type is required to add a vulnerability.");
      return;
    }
    if (!addForm.prompt.trim() || !addForm.response.trim() || !addForm.evaluation.trim()) {
      setAddError("Please fill in prompt, response, and evaluation.");
      return;
    }

    setIsAdding(true);
    setAddError("");
    try {
      await API.post("/add-vulnerability/", {
        filename: `${filename}.json`,
        selected_app,
        type: presetType,
        prompt: addForm.prompt.trim(),
        response: addForm.response.trim(),
        evaluation: addForm.evaluation.trim(),
        severity: addForm.severity
      });
      await fetchDetails();
      setShowAddModal(false);
    } catch (err) {
      setAddError("Failed to add vulnerability. Please try again.");
    } finally {
      setIsAdding(false);
    }
  };

  const renderValue = (value) => {
    if (typeof value === "object" && value !== null) {
      return (
        <pre className="whitespace-pre-wrap text-base">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }
    return value !== undefined && value !== null ? value.toString() : "N/A";
  };

  function normalizeSeverityLabel(severity) {
    if (severity === undefined || severity === null) {
      return "N/A";
    }
    const normalized = String(severity).trim().toLowerCase();
    if (normalized.length === 0) {
      return "N/A";
    }

    const numeric = Number(normalized);
    if (!Number.isNaN(numeric)) {
      if (numeric >= 9) return "Critical";
      if (numeric >= 7) return "High";
      if (numeric >= 4) return "Medium";
      if (numeric >= 1) return "Low";
      return "Info";
    }

    if (normalized.includes("critical")) return "Critical";
    if (normalized.includes("high")) return "High";
    if (normalized.includes("medium") || normalized.includes("moderate")) return "Medium";
    if (normalized.includes("low")) return "Low";
    if (normalized.includes("info") || normalized.includes("informational")) return "Info";

    return normalized
      .split(/[^a-z0-9]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }

  function normalizeSeverityValue(severity) {
    if (severity === undefined || severity === null) return "info";
    const normalized = String(severity).trim().toLowerCase();
    if (!normalized) return "info";
    const numeric = Number(normalized);
    if (!Number.isNaN(numeric)) {
      if (numeric >= 9) return "critical";
      if (numeric >= 7) return "high";
      if (numeric >= 4) return "medium";
      if (numeric >= 1) return "low";
      return "info";
    }
    if (normalized.includes("critical")) return "critical";
    if (normalized.includes("high")) return "high";
    if (normalized.includes("medium") || normalized.includes("moderate")) return "medium";
    if (normalized.includes("low")) return "low";
    if (normalized.includes("info") || normalized.includes("informational")) return "info";
    return normalized;
  }  

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ];

  const formatOrdinal = (day) => {
    if (day >= 11 && day <= 13) return `${day}th`;
    const mod = day % 10;
    if (mod === 1) return `${day}st`;
    if (mod === 2) return `${day}nd`;
    if (mod === 3) return `${day}rd`;
    return `${day}th`;
  };

  const formatFpMarkedAt = (dateString) => {
    if (!dateString) return "unknown date";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "unknown date";
    const day = formatOrdinal(date.getDate());
    const month = monthNames[date.getMonth()] || "unknown month";
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getFpTooltipText = (row) => {
    if (!row?.isFalsePositive) return null;
    const markedBy = row.fp_marked_by_email || row.fp_marked_by || "unknown user";
    const markedAt = formatFpMarkedAt(row.fp_marked_at);
    return `Marked as False Positive by ${markedBy} on ${markedAt}`;
  };
  const resolvePresetType = () => {
    if (type_filter && type_filter !== "null" && type_filter !== "undefined") {
      return type_filter;
    }
    return (
      data[0]?.type ||
      falsePositives[0]?.type ||
      passCases[0]?.type ||
      ""
    );
  };

  const resolvePresetCategory = () => {
    return (
      data[0]?.category ||
      falsePositives[0]?.category ||
      passCases[0]?.category ||
      ""
    );
  };

  const severityColorMap = {
    critical: { text: "text-red-400",     bg: "bg-red-500/10",     hoverBg: "hover:bg-red-500/20",     activeBg: "bg-red-500/25",     dot: "bg-red-400"     },
    high:     { text: "text-red-400",     bg: "bg-red-500/10",     hoverBg: "hover:bg-red-500/20",     activeBg: "bg-red-500/25",     dot: "bg-red-400"     },
    medium:   { text: "text-amber-400",   bg: "bg-amber-500/10",   hoverBg: "hover:bg-amber-500/20",   activeBg: "bg-amber-500/25",   dot: "bg-amber-400"   },
    low:      { text: "text-emerald-400", bg: "bg-emerald-500/10", hoverBg: "hover:bg-emerald-500/20", activeBg: "bg-emerald-500/25", dot: "bg-emerald-400" },
    info:     { text: "text-slate-400",   bg: "bg-slate-500/10",   hoverBg: "hover:bg-slate-500/20",   activeBg: "bg-slate-500/25",   dot: "bg-slate-400"   },
  };

  const severityOptions = [
    { value: "critical", label: "Critical" },
    { value: "high", label: "High" },
    { value: "medium", label: "Medium" },
    { value: "low", label: "Low" },
    { value: "info", label: "Info" }
  ];

  const SeverityDropdown = ({ value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selectedOption = severityOptions.find((opt) => opt.value === value);
  const colors = severityColorMap[value] || severityColorMap.info;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="relative w-full max-w-[180px]">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((open) => !open)}
        className={`w-full bg-[#232325]/90 backdrop-blur-sm border-2 ${
          isOpen
            ? "border-blue-500 shadow-lg shadow-blue-500/30 scale-[1.01]"
            : "border-[#232325] hover:border-blue-400/50 hover:shadow-md hover:shadow-blue-500/10"
        } px-3 py-2 rounded-2xl text-left text-sm focus:outline-none transition-all duration-300 cursor-pointer flex items-center justify-between`}
      >
        <span className={`flex items-center gap-2 ${selectedOption ? colors.text : "text-gray-500"}`}>
          {selectedOption && (
            <span className={`inline-block w-2 h-2 rounded-full ${colors.dot}`} />
          )}
          {selectedOption ? selectedOption.label : "Select severity"}
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-[#232325]/95 backdrop-blur-xl border border-[#232325] rounded-2xl shadow-2xl shadow-black/50 max-h-56 overflow-hidden animate-in slide-in-from-top-2 duration-200 z-50">
          <div className="max-h-56 overflow-y-auto scrollbar-thin scrollbar-track-gray-800 scrollbar-thumb-gray-600">
            {severityOptions.map((opt) => {
              const optColors = severityColorMap[opt.value] || severityColorMap.info;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center gap-2 ${
                    opt.value === value
                      ? `${optColors.activeBg} ${optColors.text}`
                      : `${optColors.text} ${optColors.hoverBg}`
                  }`}
                  onClick={() => { onChange(opt.value); setIsOpen(false); }}
                >
                  <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${optColors.dot}`} />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
const getSeverityClasses = (severity) => {
  const normalized = String(severity || "").trim().toLowerCase();
  if (normalized === "critical" || normalized === "high") {
    return "text-red-400 bg-red-500/10 border border-red-500/20 rounded-full px-2.5 py-0.5 text-xs font-medium";
  }
  if (normalized === "medium" || normalized === "moderate") {
    return "text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 rounded-full px-2.5 py-0.5 text-xs font-medium";
  }
  if (normalized === "low") {
    return "text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded-full px-2.5 py-0.5 text-xs font-medium";
  }
  if (normalized === "info" || normalized === "informational") {
    return "text-gray-400 bg-gray-500/10 border border-gray-500/20 rounded-full px-2.5 py-0.5 text-xs font-medium";
  }
  return "text-gray-400 bg-gray-500/10 border border-gray-500/20 rounded-full px-2.5 py-0.5 text-xs font-medium";
};

  const renderTable = (
    tableData,
    isFalsePositiveTable = false,
    readOnly = false,
    theme = "blue",
    showEvaluation = true,
    onSeverityChange = null
  ) => {
    const tableTheme =
      theme === "emerald"
        ? {
            border: "border-emerald-500/60",
            text: "text-emerald-400",
            divide: "divide-emerald-500/40"
          }
        : theme === "amber"
          ? {
              border: "border-amber-500/60",
              text: "text-amber-400",
              divide: "divide-amber-500/40"
            }
          : {
              border: "border-[#3B82F6]",
              text: "text-[#3B82F6]",
              divide: "divide-[#3B82F6]"
            };

    return (
      <div className="space-y-6 w-full overflow-x-auto">
        <div
          className={`bg-transparent ${tableTheme.text} border ${tableTheme.border} rounded-lg overflow-hidden`}
        >
          <table className="w-full min-w-[500px] text-left table-fixed break-words">
            <thead>
              <tr className="text-white text-base sm:text-lg">
                <th className={`px-3 sm:px-4 py-3 w-[6%] border-r ${tableTheme.border}`}>
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    onChange={(e) => {
                      if (readOnly) return;
                      if (isFalsePositiveTable) {
                        setSelectedFalsePositives(
                          e.target.checked
                            ? falsePositives.map((row) => row.testIdx)
                            : []
                        );
                      } else {
                        handleSelectAllChange(e.target.checked);
                      }
                    }}
                    checked={
                      readOnly
                        ? false
                        : isFalsePositiveTable
                          ? selectedFalsePositives.length === falsePositives.length &&
                            falsePositives.length > 0
                          : selectedPrompts.length === data.length && data.length > 0
                    }
                  />
                </th>
                <th className={`px-3 sm:px-4 py-3 w-[8%] border-r ${tableTheme.border}`}>
                  Prompt ID
                </th>
                <th className={`px-3 sm:px-4 py-3 w-[22%] border-r ${tableTheme.border}`}>
                  Category
                </th>
                <th className={`px-3 sm:px-4 py-3 w-[22%] border-r ${tableTheme.border}`}>
                  Detector
                </th>
                <th className={`px-3 sm:px-4 py-3 w-[20%] border-r ${tableTheme.border}`}>
                  Method
                </th>
                <th className="px-3 sm:px-4 py-3 w-[22%]">Severity</th>
              </tr>
            </thead>
          </table>
        </div>

        {tableData.map((row, idx) => (
          <div
            key={idx}
            className={`bg-[#101012] border ${tableTheme.border} rounded-lg overflow-x-auto text-white w-full`}
          >
            <table className="w-full min-w-[500px] text-left table-fixed">
              <tbody>
                <tr className="text-white text-base sm:text-lg">
                  <td className="px-3 sm:px-4 py-3 w-[6%]">
                    <input
                      type="checkbox"
                      disabled={readOnly}
                      checked={
                        readOnly
                          ? false
                          : isFalsePositiveTable
                            ? selectedFalsePositives.includes(row.testIdx)
                            : selectedPrompts.includes(row.testIdx)
                      }
                      onChange={(e) => {
                        if (readOnly) return;
                        if (isFalsePositiveTable) {
                          handleFalsePositiveCheckboxChange(
                            row.testIdx,
                            e.target.checked
                          );
                        } else {
                          handleCheckboxChange(row.testIdx, e.target.checked);
                        }
                      }}
                    />
                  </td>
                  <td className={`px-3 sm:px-4 py-3 w-[8%] border-l ${tableTheme.border} break-words`}>
                    {renderValue(row.testIdx)}
                  </td>
                  <td className={`px-3 sm:px-4 py-3 w-[22%] border-l ${tableTheme.border} break-words`}>
                    {renderValue(row.category)}
                  </td>
                  <td className={`px-3 sm:px-4 py-3 w-[22%] border-l ${tableTheme.border} break-words`}>
                    {renderValue(row.type)}
                  </td>
                  <td className={`px-3 sm:px-4 py-3 w-[20%] border-l ${tableTheme.border} break-words`}>
                    {renderValue(row["Strategy Used"])}
                  </td>
                  <td className={`px-3 sm:px-4 py-3 text-center align-middle w-[22%] border-l ${tableTheme.border}`}>
                      {(() => {
                        const rawSeverity = row.score || row.severity || "N/A";
                        const displaySeverity = normalizeSeverityLabel(rawSeverity);
                        const normalizedValue = normalizeSeverityValue(rawSeverity);
                      if (readOnly || !onSeverityChange) {
                        const initialRaw = row.initial_severity ?? row.initialSeverity;
                        const initialLabel = initialRaw ? normalizeSeverityLabel(initialRaw) : null;
                        const hasChanged = initialLabel && initialLabel.toLowerCase() !== displaySeverity.toLowerCase();
                        const fpTooltip = getFpTooltipText(row);

                        return (
                          <div className="inline-flex items-center gap-1.5 justify-center w-full">
                            {hasChanged ? (
                              <>
                                <span className={`inline-flex items-center px-2 py-1 text-sm font-medium rounded ${getSeverityClasses(initialLabel)}`}>
                                  {initialLabel}
                                </span>
                                <svg className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <span className={`inline-flex items-center px-2 py-1 text-sm font-medium rounded ${getSeverityClasses(displaySeverity)}`}>
                                  {displaySeverity}
                                </span>
                              </>
                            ) : (
                              <span className={`inline-flex items-center px-2.5 py-1 text-md font-medium rounded ${getSeverityClasses(displaySeverity)}`}>
                                {displaySeverity}
                              </span>
                            )}
                            {hasChanged && (
                              <HoverTooltip
                                className="cursor-default"
                                text={`Severity was changed from ${initialLabel} to ${displaySeverity} by ${row.severity_changed_by || "unknown user"}`}
                              >
                                <div className="w-4 h-4 rounded-full border border-blue-500 text-blue-400 flex items-center justify-center text-[10px] font-medium leading-none">
                                  i
                                </div>
                              </HoverTooltip>
                            )}
                            {fpTooltip && (
                              <HoverTooltip className="cursor-default" text={fpTooltip}>
                                <div className="w-3.5 h-3.5 rounded-full border border-amber-500 text-amber-400 flex items-center justify-center text-[9px] font-medium leading-none">
                                  i
                                </div>
                              </HoverTooltip>
                            )}
                          </div>
                        );
                      }
              return (
                <div className="flex items-center justify-between w-full gap-2">
                  {(() => {
                    const initialRaw = row.initial_severity ?? row.initialSeverity;
                    const initialLabel = initialRaw ? normalizeSeverityLabel(initialRaw) : null;
                    const hasChanged = initialLabel && initialLabel.toLowerCase() !== displaySeverity.toLowerCase();
                    const fpTooltip = getFpTooltipText(row);
                    if (!hasChanged) {
                      return (
                        <>
                          <div className="flex-1 min-w-0">
                            <SeverityDropdown
                              value={normalizedValue}
                              onChange={(newSeverity) => onSeverityChange(row.testIdx, newSeverity)}
                              disabled={false}
                            />
                          </div>
                          {fpTooltip && (
                            <HoverTooltip className="cursor-default" text={fpTooltip}>
                              <div className="w-3.5 h-3.5 rounded-full border border-amber-500 text-amber-400 flex items-center justify-center text-[9px] font-medium leading-none">
                                i
                              </div>
                            </HoverTooltip>
                          )}
                        </>
                      );
                    }
                    return (
                      <>
                        {/* Left: ● Initial → dropdown */}
                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                          <span className={`flex items-center gap-1.5 text-sm font-medium whitespace-nowrap ${
                            severityColorMap[normalizeSeverityValue(initialLabel)]?.text || "text-slate-400"
                          }`}>
                            <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                              severityColorMap[normalizeSeverityValue(initialLabel)]?.dot || "bg-slate-400"
                            }`} />
                            {initialLabel}
                          </span>
                          <svg className="w-3 h-3 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <SeverityDropdown
                              value={normalizedValue}
                              onChange={(newSeverity) => onSeverityChange(row.testIdx, newSeverity)}
                              disabled={false}
                            />
                          </div>
                        </div>
                        {/* Right: ⓘ */}
                        <div className="flex items-center gap-2">
                          <HoverTooltip
                            className="cursor-default"
                            text={`Severity was changed from ${initialLabel} to ${displaySeverity} by ${row.severity_changed_by || "unknown user"}`}
                          >
                            <div className="w-3.5 h-3.5 rounded-full border border-blue-500 text-blue-400 flex items-center justify-center text-[9px] font-medium leading-none">
                              i
                            </div>
                          </HoverTooltip>
                          {fpTooltip && (
                            <HoverTooltip className="cursor-default" text={fpTooltip}>
                              <div className="w-3.5 h-3.5 rounded-full border border-amber-500 text-amber-400 flex items-center justify-center text-[9px] font-medium leading-none">
                                i
                              </div>
                            </HoverTooltip>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              );
                      })()}
                    </td>
                </tr>
              </tbody>
            </table>

            <div
              className={`divide-y ${tableTheme.divide} border-t border-l border-r border-b ${tableTheme.border} text-white text-sm sm:text-base`}
            >
              <div>
                <div
                  className={`bg-transparent px-4 py-3 font-semibold ${tableTheme.text} text-base sm:text-lg`}
                >
                  Prompt
                </div>
                <div className="px-4 py-4 bg-transparent break-words whitespace-pre-wrap overflow-x-auto">
                  {renderValue(row.prompt)}
                </div>
              </div>

              <div>
                <div
                  className={`flex items-center justify-between gap-3 bg-transparent px-4 py-3 font-semibold ${tableTheme.text} text-base sm:text-lg`}
                >
                  <span>Response</span>
                  {Array.isArray(row.conversation) && row.conversation.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        convoTriggerRef.current = e.currentTarget;
                        setConvo({
                          messages: row.conversation,
                          title: row["Strategy Used"] || row.type || "Conversation",
                        });
                      }}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-300 border border-blue-400/50 rounded-lg px-2.5 py-1 hover:bg-blue-500/20 transition flex-shrink-0"
                      title="View the full multi-turn conversation"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.8L3 21l1.8-3.6A7.97 7.97 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      View full conversation
                      <span className="text-gray-400">({row.conversation.length})</span>
                    </button>
                  )}
                </div>
                <div className="px-4 py-4 bg-transparent break-words whitespace-pre-wrap overflow-x-auto">
                  {renderValue(row.response)}
                </div>
              </div>

              {showEvaluation && (
                <div>
                  <div
                    className={`bg-transparent px-4 py-3 font-semibold ${tableTheme.text} text-base sm:text-lg`}
                  >
                    Evaluation
                  </div>
                  <div className="px-4 py-4 bg-transparent break-words whitespace-pre-wrap overflow-x-auto">
                    {renderValue(row.error)}
                  </div>
                </div>
              )}

              {row.fp_feedback && (
                <div>
                  <div
                    className={`bg-transparent px-4 py-3 font-semibold ${tableTheme.text} text-base sm:text-lg`}
                  >
                    False Positive Feedback
                  </div>
                  <div className="px-4 py-4 bg-transparent break-words whitespace-pre-wrap">
                    {renderValue(row.fp_feedback)}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 min-h-screen bg-transparent text-white">
        <div className="text-center">
          <LoadingPopup isVisible={true} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 min-h-screen bg-transparent text-white">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  if (data.length === 0 && falsePositives.length === 0 && passCases.length === 0)
    return null;

  return (
    <div className="p-4 sm:p-6 bg-transparent text-white w-full flex flex-col">
      {showFpFeedback && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fpFade"
            onClick={handleFpFeedbackClose}
          />
          <div className="relative w-[90vw] max-w-2xl rounded-3xl border border-amber-500/30 bg-gradient-to-br from-[#151517] via-[#1a1a1d] to-[#101012] shadow-2xl shadow-amber-500/10 animate-fpRise">
            <div className="flex items-center justify-between px-6 pt-6">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                  <span className="text-amber-300 text-sm font-semibold">FP</span>
                </div>
                <div>
                  <div className="text-xl font-semibold text-amber-200">Mark as False Positive</div>
                  <div className="text-sm text-amber-200/60">
                    {selectedPrompts.length} {selectedPrompts.length === 1 ? "item" : "items"} selected
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleFpFeedbackClose}
                className="w-9 h-9 rounded-full border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 transition"
                aria-label="Close"
              >
                X
              </button>
            </div>
            <div className="px-6 pb-6 pt-4">
              <label className="block text-sm text-amber-100/70 mb-2">
                Why is this a false positive?
              </label>
              <textarea
                value={fpFeedback}
                onChange={(e) => setFpFeedback(e.target.value)}
                rows={4}
                placeholder="Add a short reason so the team can learn from it."
                className="w-full rounded-2xl bg-[#121214] border border-amber-500/20 focus:border-amber-400/60 outline-none px-4 py-3 text-sm text-amber-100 placeholder:text-amber-100/30 transition"
              />
              {fpFeedbackError && (
                <div className="mt-2 text-sm text-red-400">{fpFeedbackError}</div>
              )}
              <div className="mt-5 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleFpFeedbackClose}
                  className="px-4 py-2 rounded-full border border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleFpFeedbackSubmit}
                  disabled={fpSubmitting}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    fpSubmitting
                      ? "bg-amber-700/40 text-amber-200 cursor-not-allowed"
                      : "bg-amber-500 text-black hover:bg-amber-400 shadow-lg shadow-amber-500/20"
                  }`}
                >
                  {fpSubmitting ? "Saving..." : "Save & Mark FP"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAddModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fpFade"
            onClick={closeAddModal}
          />
          <div className="relative w-[92vw] max-w-3xl rounded-3xl border border-blue-500/20 bg-gradient-to-br from-[#131316] via-[#1b1b1f] to-[#0f0f12] shadow-2xl shadow-blue-500/10 animate-fpRise">
            <div className="flex items-center justify-between px-6 pt-6">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                  <span className="text-blue-300 text-lg font-semibold">+</span>
                </div>
                <div>
                  <div className="text-xl font-semibold text-blue-200">Add Vulnerability</div>
                  <div className="text-sm text-blue-200/60">Create a manual entry</div>
                </div>
              </div>
              <button
                type="button"
                onClick={closeAddModal}
                className="w-9 h-9 rounded-full border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 transition"
                aria-label="Close"
              >
                X
              </button>
            </div>
            <div className="px-6 pb-6 pt-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-blue-200/70 mb-2">Category</label>
                  <input
                    value={resolvePresetCategory() || "N/A"}
                    readOnly
                    className="w-full rounded-2xl bg-[#121214] border border-blue-500/20 px-4 py-3 text-sm text-blue-100/80"
                  />
                </div>
                <div>
                  <label className="block text-sm text-blue-200/70 mb-2">Type</label>
                  <input
                    value={resolvePresetType() || "N/A"}
                    readOnly
                    className="w-full rounded-2xl bg-[#121214] border border-blue-500/20 px-4 py-3 text-sm text-blue-100/80"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-blue-200/70 mb-2">Prompt</label>
                <textarea
                  value={addForm.prompt}
                  onChange={(e) => setAddForm((prev) => ({ ...prev, prompt: e.target.value }))}
                  rows={3}
                  placeholder="Paste or write the prompt..."
                  className="w-full rounded-2xl bg-[#121214] border border-blue-500/20 focus:border-blue-400/60 outline-none px-4 py-3 text-sm text-blue-100 placeholder:text-blue-100/30 transition"
                />
              </div>

              <div>
                <label className="block text-sm text-blue-200/70 mb-2">Response</label>
                <textarea
                  value={addForm.response}
                  onChange={(e) => setAddForm((prev) => ({ ...prev, response: e.target.value }))}
                  rows={3}
                  placeholder="Model response..."
                  className="w-full rounded-2xl bg-[#121214] border border-blue-500/20 focus:border-blue-400/60 outline-none px-4 py-3 text-sm text-blue-100 placeholder:text-blue-100/30 transition"
                />
              </div>

              <div>
                <label className="block text-sm text-blue-200/70 mb-2">Evaluation</label>
                <textarea
                  value={addForm.evaluation}
                  onChange={(e) => setAddForm((prev) => ({ ...prev, evaluation: e.target.value }))}
                  rows={3}
                  placeholder="Explain the issue..."
                  className="w-full rounded-2xl bg-[#121214] border border-blue-500/20 focus:border-blue-400/60 outline-none px-4 py-3 text-sm text-blue-100 placeholder:text-blue-100/30 transition"
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div className="w-full">
                  <label className="block text-sm text-blue-200/70 mb-2">Severity</label>
                  <SeverityDropdown
                    value={addForm.severity}
                    onChange={(newSeverity) => setAddForm((prev) => ({ ...prev, severity: newSeverity }))}
                    disabled={false}
                  />
                </div>
              </div>

              {addError && <div className="text-sm text-red-400">{addError}</div>}

              <div className="mt-2 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeAddModal}
                  className="px-4 py-2 rounded-full border border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddVulnerability}
                  disabled={isAdding}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    isAdding
                      ? "bg-blue-700/40 text-blue-200 cursor-not-allowed"
                      : "bg-blue-500 text-black hover:bg-blue-400 shadow-lg shadow-blue-500/20"
                  }`}
                >
                  {isAdding ? "Adding..." : "Add Vulnerability"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex-grow min-h-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h2 className="text-2xl sm:text-4xl font-bold text-white">Vulnerabilities</h2>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            {saveMessage && (
              <span
                className={`text-sm ${
                  saveMessage.toLowerCase().includes("failed")
                    ? "text-red-400"
                    : "text-emerald-400"
                }`}
              >
                {saveMessage}
              </span>
            )}
            <button
              onClick={handleSaveSeverity}
              disabled={isSaving || Object.keys(severityEdits).length === 0}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                isSaving || Object.keys(severityEdits).length === 0
                  ? "bg-slate-700 text-slate-400 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-500 shadow-lg shadow-blue-500/20"
              }`}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {renderTable(
          [...data].sort((a, b) => parseInt(a.testIdx) - parseInt(b.testIdx)),
          false,
          false,
          "blue",
          true,
          (testIdx, newSeverity) => {
            setData((prev) =>
              prev.map((row) =>
                row.testIdx === testIdx
                  ? { ...row, score: newSeverity, severity: newSeverity }
                  : row
              )
            );
            setSeverityEdits((prev) => {
              const next = { ...prev };
              if (initialSeverityMap[testIdx] === newSeverity) {
                delete next[testIdx];
              } else {
                next[testIdx] = newSeverity;
              }
              return next;
            });
          }
        )}
        {selectedPrompts.length > 0 && (
          <div className="mt-4">
            <button
              onClick={handleSubmit}
              className="px-4 py-2 border border-blue-500 text-blue-400 rounded-full text-sm font-medium hover:bg-blue-500 hover:text-white transition duration-150 ease-in-out text-center"
            >
              Mark as False Positive
            </button>
          </div>
        )}

        {falsePositives.length > 0 && (
          <div className="mt-8 w-full">
            <button
              onClick={() => setShowFalsePositives(!showFalsePositives)}
              className="w-full group relative"
            >
              <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-900 border border-amber-600/40 rounded-lg hover:border-amber-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-amber-500/20">
                <div className="flex items-center gap-4">
                  <div
                    className={`transform transition-transform duration-500 ${showFalsePositives ? "rotate-180" : ""}`}
                  >
                    <svg
                      className="w-6 h-6 text-amber-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      />
                    </svg>
                  </div>

                  <div className="text-left">
                    <div className="text-amber-400 font-semibold text-lg">
                      {showFalsePositives ? "Hide" : "Show"} False Positives
                    </div>
                    <div className="text-amber-600/70 text-sm">
                      {falsePositives.length}{" "}
                      {falsePositives.length === 1 ? "item" : "items"} marked as
                      false positive
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-center w-10 h-10 bg-amber-600/20 border border-amber-500/50 rounded-full group-hover:bg-amber-600/30 transition-colors duration-300">
                  <span className="text-amber-400 font-bold text-lg">
                    {falsePositives.length}
                  </span>
                </div>
              </div>
            </button>

            <div
              className={`transition-all duration-500 ease-in-out ${
                showFalsePositives
                  ? "max-h-[none] opacity-100 mt-4 overflow-x-auto"
                  : "max-h-0 opacity-0 overflow-hidden"
              }`}
            >
              <div className="space-y-4 p-3 sm:p-4 bg-gradient-to-b from-slate-900/50 to-transparent rounded-lg border border-amber-600/20 animate-fadeIn min-h-0">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-6 bg-gradient-to-b from-amber-500 to-amber-600 rounded-full"></div>
                  <h3 className="text-xl sm:text-2xl font-bold text-amber-400">
                    False Positives
                  </h3>
                </div>

                <div className="animate-slideUp overflow-x-auto">
                  {renderTable(
                    [...falsePositives].sort(
                      (a, b) => parseInt(a.testIdx) - parseInt(b.testIdx)
                    ),
                    true,
                    false,
                    "amber",
                    true,
                    null
                  )}
                </div>

                {selectedFalsePositives.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-amber-600/20">
                    <button
                      onClick={handleUnmarkFalsePositive}
                      className="px-4 py-2 border border-amber-500 text-amber-400 rounded-full text-sm font-medium hover:bg-amber-500 hover:text-white transition duration-150 ease-in-out text-center"
                    >
                      Mark as True Positive
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {passCases.length > 0 && (
          <div className="mt-8 w-full">
            <button
              onClick={() => setShowPassCases(!showPassCases)}
              className="w-full group relative"
            >
              <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-emerald-900/40 to-slate-900 border border-emerald-600/40 rounded-lg hover:border-emerald-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/20">
                <div className="flex items-center gap-4">
                  <div
                    className={`transform transition-transform duration-500 ${showPassCases ? "rotate-180" : ""}`}
                  >
                    <svg
                      className="w-6 h-6 text-emerald-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="text-emerald-400 font-semibold text-lg">
                      {showPassCases ? "Hide" : "Show"} Passed Tests
                    </div>
                    <div className="text-emerald-600/70 text-sm">
                      {passCases.length} {passCases.length === 1 ? "item" : "items"} passed
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center w-10 h-10 bg-emerald-600/20 border border-emerald-500/50 rounded-full group-hover:bg-emerald-600/30 transition-colors duration-300">
                  <span className="text-emerald-400 font-bold text-lg">
                    {passCases.length}
                  </span>
                </div>
              </div>
            </button>

            <div
              className={`transition-all duration-500 ease-in-out ${
                showPassCases
                  ? "max-h-[none] opacity-100 mt-4 overflow-x-auto"
                  : "max-h-0 opacity-0 overflow-hidden"
              }`}
            >
              <div className="space-y-4 p-3 sm:p-4 bg-gradient-to-b from-emerald-900/30 to-transparent rounded-lg border border-emerald-600/30 shadow-lg shadow-emerald-500/20 animate-fadeIn min-h-0">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-6 bg-gradient-to-b from-emerald-500 to-emerald-600 rounded-full"></div>
                  <h3 className="text-xl sm:text-2xl font-bold text-emerald-400">Passed Tests</h3>
                </div>
                <div className="animate-slideUp overflow-x-auto">
                  {renderTable(
                    [...passCases].sort(
                      (a, b) => parseInt(a.testIdx) - parseInt(b.testIdx)
                    ),
                    false,
                    true,
                    "emerald",
                    false
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="mt-auto pt-4">
        <button
          onClick={() => navigate(`/details/${filename}`)}
          className="px-4 py-2 border border-blue-500 text-blue-400 rounded-full text-sm font-medium hover:bg-blue-500 hover:text-white transition duration-150 ease-in-out text-center"
        >
          Back to Details
        </button>
      </div>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[1000]">
        <button
          type="button"
          onClick={openAddModal}
          className="relative group w-14 h-14 rounded-full bg-gradient-to-br from-blue-500/80 to-indigo-500/80 text-white text-3xl font-semibold flex items-center justify-center shadow-xl shadow-blue-500/30 border border-blue-400/50 backdrop-blur-md transition-transform duration-300 hover:scale-110 animate-plusFloat"
        >
          <span className="absolute inset-0 rounded-full ring-2 ring-blue-400/50 animate-plusPulse" />
          <span className="absolute -inset-3 rounded-full bg-blue-500/10 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <span className="relative leading-none">+</span>
        </button>
      </div>

      {convo &&
        createPortal(
          <div className="fixed inset-0 z-[10000] flex justify-end">
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fadeIn"
              onClick={() => setConvo(null)}
            />
            <div
              ref={drawerRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="convo-drawer-title"
              tabIndex={-1}
              className="relative h-full w-full max-w-md sm:max-w-lg bg-[#121214] border-l border-[#232325] shadow-2xl flex flex-col animate-slideInRight focus:outline-none"
            >
              <div className="flex items-center justify-between gap-2 px-5 py-4 border-b border-[#232325] flex-shrink-0">
                <div className="min-w-0">
                  <h3 id="convo-drawer-title" className="text-white font-semibold">
                    Full conversation
                  </h3>
                  <p className="text-xs text-gray-400 truncate">
                    {convo.title} · {convo.messages.length} messages
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      const text = (convo.messages || [])
                        .map((m) => {
                          const r = String(m?.role || "message").toLowerCase();
                          const who =
                            r === "user"
                              ? "Attacker"
                              : r === "assistant"
                                ? "Target"
                                : r;
                          const c =
                            typeof m?.content === "string"
                              ? m.content
                              : m?.content == null
                                ? ""
                                : JSON.stringify(m.content, null, 2);
                          return `${who}: ${c}`;
                        })
                        .join("\n\n");
                      navigator.clipboard?.writeText(text);
                    }}
                    aria-label="Copy full transcript"
                    title="Copy full transcript"
                    className="text-gray-400 hover:text-white text-xs border border-[#232325] rounded-lg px-2.5 py-1 transition hover:bg-white/5"
                  >
                    Copy
                  </button>
                  <button
                    type="button"
                    onClick={() => setConvo(null)}
                    aria-label="Close conversation"
                    className="text-gray-400 hover:text-white text-2xl leading-none w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white/5 transition"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
                {(convo.messages || []).map((m, i) => {
                  const role = String(m?.role || "").toLowerCase();
                  const hasContent =
                    m?.content !== undefined &&
                    m?.content !== null &&
                    m?.content !== "";
                  const content = !hasContent
                    ? ""
                    : typeof m.content === "string"
                      ? m.content
                      : JSON.stringify(m.content, null, 2);
                  const body = content || (
                    <span className="italic text-gray-500">(empty)</span>
                  );
                  const isUser = role === "user";
                  const isSystem = role === "system";
                  const label = isUser
                    ? "Attacker"
                    : role === "assistant"
                      ? "Target"
                      : m?.role || "Message";

                  if (isSystem) {
                    return (
                      <div key={i} className="text-center">
                        <span className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">
                          System #{i + 1}
                        </span>
                        <div className="text-xs text-gray-400 bg-[#1a1a1c] border border-[#232325] rounded-lg px-3 py-2 whitespace-pre-wrap break-words text-left">
                          {body}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={i}
                      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`flex flex-col max-w-[85%] ${
                          isUser ? "items-end" : "items-start"
                        }`}
                      >
                        <span
                          className={`flex items-center gap-1.5 text-[11px] uppercase tracking-wide mb-1 ${
                            isUser ? "text-blue-300" : "text-emerald-300"
                          }`}
                        >
                          {label}
                          <span className="text-gray-600 normal-case tracking-normal">
                            #{i + 1}
                          </span>
                        </span>
                        <div
                          className={`rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap break-words ${
                            isUser
                              ? "bg-blue-600/20 border border-blue-500/40 text-blue-50"
                              : "bg-[#1a1a1c] border border-[#232325] text-gray-200"
                          }`}
                        >
                          {body}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Vulnerabilities;

if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = `
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes fpFade {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes fpRise {
      from { opacity: 0; transform: translateY(12px) scale(0.98); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }

    @keyframes plusPulse {
      0% { transform: scale(1); opacity: 0.6; }
      70% { transform: scale(1.5); opacity: 0; }
      100% { transform: scale(1.5); opacity: 0; }
    }

    @keyframes plusFloat {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-4px); }
    }

    @keyframes slideInRight {
      from { transform: translateX(100%); }
      to { transform: translateX(0); }
    }

    .animate-fadeIn {
      animation: fadeIn 0.3s ease-in-out;
    }

    .animate-slideUp {
      animation: slideUp 0.4s ease-in-out;
    }

    .animate-fpFade {
      animation: fpFade 0.18s ease-out;
    }

    .animate-fpRise {
      animation: fpRise 0.22s ease-out;
    }

    .animate-plusPulse {
      animation: plusPulse 2.4s ease-out infinite;
    }

    .animate-plusFloat {
      animation: plusFloat 4.5s ease-in-out infinite;
    }

    .animate-slideInRight {
      animation: slideInRight 0.25s ease-out;
    }
  `;
  document.head.appendChild(style);
}




